<template>
  <div class="image-upload" :class="['dropbox', { active: Boolean(imageSrc) }]">
    <label>{{ label }}</label>

    <input
      ref="input"
      type="file"
      accept="image/jpeg, image/png"
      :name="name"
      @change="handleChange"
    />

    <img
      v-show="imageSrc"
      :src="imageSrc"
      :width="width"
      :height="height"
    />

    <a
      v-show="imageSrc"
      class="remove link"
      @click="remove"
    >
      Remove
    </a>

    <div v-show="!imageSrc" class="empty">
      <icon class="icon" :name="icon" />
      <div>{{ placeholder }}</div>
    </div>
  </div>
</template>

<script>
import Compressor from 'compressorjs';
import * as Sentry from '@sentry/vue';

export default {
  props: {
    value: [File, String, Blob],
    name: String,
    label: String,
    placeholder: {
      type: String,
      default: 'Drag and drop an image or click to browse files',
    },
    icon: {
      type: String,
      default: 'upload_file',
    },
    width: {
      type: String,
      default: 'auto',
    },
    height: {
      type: String,
      default: 'auto',
    },
  },

  data() {
    return {
      localValue: this.value,
      imageSrc: null,
    };
  },

  created() {
    this.setImageSrc();
  },

  watch: {
    value(value) {
      this.localValue = value;
      this.setImageSrc();
    },

    localValue(value) {
      this.$emit('input', value);
    },
  },

  methods: {
    async handleChange(event) {
      const { files } = event.target;

      if (!files.length) return;

      const file = files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        this.imageSrc = reader.result;
        this.$emit('result', reader.result);
      };

      new Compressor(file, {
        quality: 0.6,
        success: (compressed) => {
          reader.readAsDataURL(compressed);
          this.$emit('input', compressed);
        },
        error(e) {
          alert(e.message);
          Sentry.captureException(e);
        },
      });
    },

    setImageSrc() {
      if (this.value instanceof Blob) {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.imageSrc = reader.result;
        };
        reader.readAsDataURL(this.value);
      } else if (typeof this.value === 'string') {
        this.imageSrc = this.value;
      } else {
        this.imageSrc = null;
      }
    },

    remove() {
      this.$refs.input.value = '';
      this.imageSrc = null;
      this.$emit('input', null);
    },
  },
};
</script>

<style lang="scss" scoped>
.dropbox {
  outline: 1px solid var(--theme-input-border);
  padding: $gp * .5;
  min-height: var(--image-upload-min-height, 320px);
  min-width: 200px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  margin-bottom: $gp;
  border-radius: $border-radius;
  position: relative;

  &.active {
    input {
      pointer-events: none;
    }
  }
}

label {
  color: $color-gray-5;
  background-color: var(--theme-background);
  font-size: 12px;
  position: absolute;
  transform-origin: top left;
  padding: 0 3px;
  top: 0;
  left: 0;
  transform: translate(13px, -9px);
  font-weight: bold;
}

input {
  opacity: 0;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  cursor: pointer;
}

img {
  border-radius: $border-radius;
  object-fit: cover;
  max-width: 100%;
  max-height: 100%;
}

.remove {
  font-size: 14px;
  margin-top: $gp * .5;
}

.empty {
  max-width: 150px;
  color: $color-gray-5;
  font-size: 14px;

  .icon {
    font-size: 48px;
    margin-bottom: $gp * .5;
  }
}
</style>
